import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ADJUSTMENT_DATE_KEY,
  ADJUSTMENT_KEY,
  BAD_DEBT_DATE_KEY,
  BAD_DEBT_KEY,
  COMPANY_NAME_KEY,
  CREATED_AT_KEY,
  DEAL_DUE_KEY,
  DEAL_KEY,
  DEAL_STAGE_KEY,
  ENGAGEMENT_MANAGER_KEY,
  NET_VALUE_KEY,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  PAY_1_DATE_KEY,
  PAY_2_DATE_KEY,
  PAY_3_DATE_KEY,
  PAYMENT_1_KEY,
  PAYMENT_2_KEY,
  PAYMENT_3_KEY,
  REFUND_DATE_KEY,
  REFUND_KEY,
  RENEWAL_RATE_KEY,
  SPLIT_KEY,
  STATUS_KEY,
  SUPPORTING_DUE_DATE_KEY,
  VALUATION_DATE_KEY,
  VALUATION_FEE_KEY,
} from 'common/constants/analyst-dashboards';
import { AttioQueryKeys } from 'dashboard409a/common/types/commonTypes';
import { addBusinessDays } from 'dashboard409a/components/OperationsModal/useOperationsModal';
import { useCellEditEnd } from 'dashboard409a/hooks/useCellEditEnd';
import { DealStageState } from 'dashboard409a/pages/Operations/components/ValuationDealInfo/ValuationDealInfo.props';
import { updateValuationStatus } from 'dashboard409a/services/attio-common';
import { Note } from 'dashboard409a/services/models.types';
import { executeFulfillmentSideEffectsForStatusChange } from 'dashboard409a/utilities/fulfillmentOnStatusChange';
import { Logger } from 'utilities';
import { StyledSpanEmptyStatus, StyledStatusChip, StyledTableCell, StyledTableRow } from '../common';
import InlineNoteEditor from '../common/InlineNoteEditor';
import { StatusDropdown } from '../StatusDropdown';
import { ValuationsTableRowProps } from './ValuationsTableRow.types';
import { EnhancedTableHeadColumn } from '../ValuationsTable.types';
import CompanyNameCell from './cells/CompanyNameCell';
import ConsultantCell from './cells/ConsultantCell';
import DealCell from './cells/DealCell';
import DealDueCell from './cells/DealDueCell';
import DealStageCell from './cells/DealStageCell';
import EngagementManagerCell from './cells/EngagementManagerCell';
import FinancialFieldCell from './cells/FinancialFieldCell';

const getCellWidth = (columnWidths: Record<string, number> | undefined, columnId: string, defaultWidth: string) => {
  if (columnWidths?.[columnId]) {
    return {
      width: `${columnWidths[columnId]}px`,
      minWidth: `${columnWidths[columnId]}px`,
      maxWidth: `${columnWidths[columnId]}px`,
    };
  }
  return { width: defaultWidth };
};

const StatusCellDisplay = ({
  status,
  isUpdating,
  isFinal,
  onStatusClick,
}: {
  status: string | null | undefined;
  isUpdating: boolean;
  isFinal: boolean;
  onStatusClick: (e: React.MouseEvent) => void;
}) => {
  const cursor = isUpdating || isFinal ? 'not-allowed' : 'pointer';
  const opacity = isUpdating ? 0.6 : 1;
  let label: string;
  if (isUpdating) {
    label = 'Updating...';
  } else if (status) {
    label = status;
  } else {
    label = 'Click to select status';
  }
  if (status) {
    return (
      <StyledStatusChip
        label={label}
        status={status}
        onClick={onStatusClick}
        style={{ cursor, opacity }}
        title={isFinal ? 'Final status cannot be changed' : undefined}
      />
    );
  }
  return (
    <StyledSpanEmptyStatus onClick={onStatusClick} style={{ cursor, color: '#666', fontStyle: 'italic', opacity }}>
      {label}
    </StyledSpanEmptyStatus>
  );
};

const ValuationsTableRow = ({
  valuationRow,
  onRowClick,
  onStatusUpdate,
  onNoteUpdate,
  onNoteSave,
  statusOptions,
  statusOptionsLoading,
  isNoteLoading = false,
  columns,
  columnWidths,
  onDealStageChange,
  dealStageOptions = [],
  onEngagementManagerUpdate,
  tabIndex,
  onFocus,
  rowIndex,
  isNoteSelected = false,
  onNoteSelect,
  onNoteDoubleClick,
  onNoteMouseDown,
  onNoteMouseEnter,
}: ValuationsTableRowProps) => {
  const queryClient = useQueryClient();
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isNoteCellActive, setIsNoteCellActive] = useState(false);
  const [dealStageState, setDealStageState] = useState<DealStageState>(DealStageState.IDLE);
  const statusCellRef = useRef<HTMLTableCellElement>(null);
  const dealStageCellRef = useRef<HTMLTableCellElement>(null);

  const handleStatusEditEnd = useCellEditEnd(statusCellRef, () => setIsEditingStatus(false));

  // Check if status is final and should be read-only
  const isFinalStatus = valuationRow.status === 'Final Sent';

  const handleStatusChange = async (newStatus: string, statusOptionId: string) => {
    try {
      setIsUpdatingStatus(true);

      // Update status via API
      await updateValuationStatus(valuationRow.record_id, statusOptionId);

      // Run fulfillment side effects (create/update) when status is Client Docs or Client Approval
      await executeFulfillmentSideEffectsForStatusChange(valuationRow.record_id, newStatus, {
        supportingDueDate: addBusinessDays(new Date(), 4),
        queryClient,
      });

      // Update local data and notify parent
      const updatedRow = { ...valuationRow, status: newStatus };
      onStatusUpdate?.(updatedRow);

      // Invalidate the list and the individual detail cache so both the table
      // and the detail modal reflect the new status. Runs after the PATCH
      // completes — no race with stale Attio data.
      queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.VALUATIONS_ALL] });
      queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationRow.record_id] });

      Logger.info('Status successfully updated to:', newStatus);
      setIsEditingStatus(false);
    } catch (error) {
      Logger.error('Error updating status:', error);
      // Could add toast notification here for better UX
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isUpdatingStatus && !isFinalStatus) {
      setIsEditingStatus(true);
    }
  };

  const handleNoteSave = async (newNote: string) => {
    if (onNoteSave) {
      await onNoteSave(newNote, valuationRow.note?.content || '');
    } else {
      onNoteUpdate?.({ ...valuationRow, note: { content: newNote } as Note });
    }
  };

  const handleDealStageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dealStageState === DealStageState.IDLE) {
      setDealStageState(DealStageState.EDITING);
    }
  };

  const handleDealStageEditEnd = useCellEditEnd(dealStageCellRef, () => setDealStageState(DealStageState.IDLE));

  const handleDealStageChange = async (value: { id: string; label: string } | null) => {
    if (!value || !onDealStageChange) {
      handleDealStageEditEnd();
      return;
    }

    setDealStageState(DealStageState.UPDATING);
    try {
      await onDealStageChange(value.id);
    } finally {
      handleDealStageEditEnd();
    }
  };

  // Financial fields configuration
  const financialFields = [
    { columnId: NET_VALUE_KEY, fieldKey: 'net_value' as const },
    { columnId: SPLIT_KEY, fieldKey: 'split' as const },
    { columnId: PAYMENT_1_KEY, fieldKey: 'payment_1' as const },
    { columnId: PAY_1_DATE_KEY, fieldKey: 'pay_1_date' as const },
    { columnId: PAYMENT_2_KEY, fieldKey: 'payment_2' as const },
    { columnId: PAY_2_DATE_KEY, fieldKey: 'pay_2_date' as const },
    { columnId: PAYMENT_3_KEY, fieldKey: 'payment_3' as const },
    { columnId: PAY_3_DATE_KEY, fieldKey: 'pay_3_date' as const },
    { columnId: REFUND_KEY, fieldKey: 'refund' as const },
    { columnId: REFUND_DATE_KEY, fieldKey: 'refund_date' as const },
    { columnId: ADJUSTMENT_KEY, fieldKey: 'adjustment' as const },
    { columnId: ADJUSTMENT_DATE_KEY, fieldKey: 'adjustment_date' as const },
    { columnId: BAD_DEBT_KEY, fieldKey: 'bad_debt' as const },
    { columnId: BAD_DEBT_DATE_KEY, fieldKey: 'bad_debt_date' as const },
    { columnId: RENEWAL_RATE_KEY, fieldKey: 'renewal_rate' as const },
  ];

  return (
    <StyledTableRow
      hover
      tabIndex={tabIndex ?? -1}
      key={valuationRow.record_id}
      onFocus={() => rowIndex !== undefined && onFocus?.(rowIndex)}
      onClick={event => onRowClick(valuationRow, event)}>
      <StyledTableCell
        align="left"
        sx={{
          position: 'sticky',
          left: 0,
          zIndex: 3,
          backgroundColor: '#ffffff',
          boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)',
          width: columnWidths?.[COMPANY_NAME_KEY] ? `${columnWidths[COMPANY_NAME_KEY]}px` : '150px',
          minWidth: columnWidths?.[COMPANY_NAME_KEY] ? `${columnWidths[COMPANY_NAME_KEY]}px` : '150px',
          maxWidth: columnWidths?.[COMPANY_NAME_KEY] ? `${columnWidths[COMPANY_NAME_KEY]}px` : '150px',
          '&:focus': {
            background: '#EDF4FB',
          },
          'tr:focus-within &': {
            backgroundColor: 'rgba(25, 118, 210, 0.06)',
          },
        }}>
        <CompanyNameCell valuationRow={valuationRow} />
      </StyledTableCell>
      <StyledTableCell
        ref={statusCellRef}
        align="left"
        onClick={handleStatusClick}
        sx={{
          width: columnWidths?.[STATUS_KEY] ? `${columnWidths[STATUS_KEY]}px` : '200px',
          minWidth: columnWidths?.[STATUS_KEY] ? `${columnWidths[STATUS_KEY]}px` : '200px',
        }}>
        <StatusDropdown
          currentStatus={valuationRow.status}
          onStatusChange={handleStatusChange}
          isEditing={isEditingStatus}
          onEditEnd={handleStatusEditEnd}
          statusOptions={statusOptions}
          loading={statusOptionsLoading}
        />

        {!isEditingStatus && (
          <StatusCellDisplay
            status={valuationRow.status}
            isUpdating={isUpdatingStatus}
            isFinal={isFinalStatus}
            onStatusClick={handleStatusClick}
          />
        )}
      </StyledTableCell>
      <StyledTableCell
        align="left"
        onClick={e => e.stopPropagation()}
        sx={{
          ...getCellWidth(columnWidths, NOTES_KEY, '220px'),
          maxWidth: columnWidths?.[NOTES_KEY] ? `${columnWidths[NOTES_KEY]}px` : '220px',
          p: 0,
          overflow: 'hidden',
          boxShadow: isNoteCellActive ? 'inset 0 0 0 1px #1976d2' : 'none',
          '&:hover': {
            backgroundColor: isNoteSelected ? 'rgba(25, 118, 210, 0.14)' : 'rgba(0, 0, 0, 0.04)',
          },
          '&:focus-visible': {
            outline: '2px solid #1976d2',
            outlineOffset: '-2px',
            backgroundColor: isNoteSelected ? 'rgba(25, 118, 210, 0.14)' : 'rgba(25, 118, 210, 0.06)',
          },
        }}>
        <InlineNoteEditor
          value={valuationRow.note?.content}
          onSave={handleNoteSave}
          isLoading={isNoteLoading}
          onActiveStateChange={setIsNoteCellActive}
        />
      </StyledTableCell>
      {columns.some((column: EnhancedTableHeadColumn) => column.id === OTHER_CONSULTANT_KEY) && (
        <StyledTableCell
          align="left"
          sx={getCellWidth(columnWidths, OTHER_CONSULTANT_KEY, 'auto')}
          onClick={e => e.stopPropagation()}>
          <ConsultantCell valuationRow={valuationRow} />
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === ENGAGEMENT_MANAGER_KEY) && (
        <StyledTableCell
          align="left"
          sx={getCellWidth(columnWidths, ENGAGEMENT_MANAGER_KEY, 'auto')}
          onClick={e => e.stopPropagation()}>
          <EngagementManagerCell valuationRow={valuationRow} onEngagementManagerUpdate={onEngagementManagerUpdate} />
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === VALUATION_DATE_KEY) && (
        <StyledTableCell sx={getCellWidth(columnWidths, VALUATION_DATE_KEY, '100px')} align="left">
          {valuationRow.valuation_date}
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === CREATED_AT_KEY) && (
        <StyledTableCell sx={getCellWidth(columnWidths, CREATED_AT_KEY, '100px')} align="left">
          {valuationRow.created_at ? valuationRow.created_at.split('T')[0] : '-'}
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === VALUATION_FEE_KEY) && (
        <StyledTableCell sx={getCellWidth(columnWidths, VALUATION_FEE_KEY, '100px')} align="left">
          {valuationRow.valuation_fee}
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === SUPPORTING_DUE_DATE_KEY) && (
        <StyledTableCell sx={getCellWidth(columnWidths, SUPPORTING_DUE_DATE_KEY, '120px')} align="left">
          {valuationRow.supporting_due_date || '-'}
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === DEAL_KEY) && (
        <StyledTableCell align="left" sx={getCellWidth(columnWidths, DEAL_KEY, 'auto')}>
          <DealCell valuationRow={valuationRow} />
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === DEAL_DUE_KEY) && (
        <StyledTableCell align="left" sx={getCellWidth(columnWidths, DEAL_DUE_KEY, 'auto')}>
          <DealDueCell valuationRow={valuationRow} />
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === DEAL_STAGE_KEY) && (
        <StyledTableCell
          ref={dealStageCellRef}
          align="left"
          sx={getCellWidth(columnWidths, DEAL_STAGE_KEY, 'auto')}
          onClick={handleDealStageClick}
          onBlur={e => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setDealStageState(DealStageState.IDLE);
            }
          }}>
          <DealStageCell
            valuationRow={valuationRow}
            dealStageOptions={dealStageOptions}
            dealStageState={dealStageState}
            onDealStageClick={handleDealStageClick}
            onDealStageChange={handleDealStageChange}
            onDealStageEditEnd={handleDealStageEditEnd}
          />
        </StyledTableCell>
      )}
      {financialFields.map(({ columnId, fieldKey }) =>
        columns.some((column: EnhancedTableHeadColumn) => column.id === columnId) ? (
          <StyledTableCell key={columnId} align="left" sx={getCellWidth(columnWidths, columnId, 'auto')}>
            <FinancialFieldCell valuationRow={valuationRow} fieldKey={fieldKey} />
          </StyledTableCell>
        ) : null
      )}
    </StyledTableRow>
  );
};
export default ValuationsTableRow;
